<!--
    The neighborhoods map as a plain map with a PDF download, rather than the
    interactive map with selectable regions and cards below it (Asana O2-73).

    Draws the SAME SvgsChicago2026 artwork the interactive map uses, so the two
    cannot drift and it stays crisp at any size. The first attempt at this used
    a PNG exported from Figma and looked poor — a raster of a map full of hairline
    streets and small labels is exactly the case where a bitmap falls apart.

    Non-interactive without touching the shared component: markActive() in
    chicago-2026.vue early-returns unless the clicked region is in `items`, so
    passing an empty array leaves the map pannable but makes every region
    unselectable. Nothing is emitted, no active class is applied, and there is
    no carousel underneath.
-->
<script setup>
    const props = defineProps({
        items: {
            type: Array,
            required: true,
            default: [],
        },
        isMapShown: {
            type: Boolean,
            required: true,
        },
    });

    const { isMapShown } = toRefs(props);

    const downloadPDF = () => {
        const link = document.createElement('a');
        link.href = '/2026-chicago-map.pdf';
        link.download = '2026-chicago-map.pdf';
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };
</script>

<template>
    <section class="svg-map-with-download">
        <!-- `items` is deliberately empty — see the note at the top of the file.
             The map still draws every region; it just cannot select one. -->
        <SvgsChicago2026 :isMapShown="isMapShown" :items="[]" selectedId="" />
        <button class="download-button small-button" @click="downloadPDF">Download PDF</button>
    </section>
</template>

<style lang="scss">
    .svg-map-with-download {
        position: relative;

        // The carousel version subtracts 162px here for the card strip. There
        // are no cards, so the map takes the height the strip used to share.
        //
        // The artwork does not fill the container — the city is not a rectangle,
        // and panning moves it around inside the frame — so whatever sits behind
        // it shows. White read as blank space; this is the same grey the map's
        // own grid is drawn on, and the same value the 2025 version used, so the
        // surround reads as part of the map rather than as a gap in the page.
        .svg-container {
            height: var(--distance-to-bottom-nav);
            background-color: #4E4E4E;
        }

        // The map is the last thing on the page, so without this it sits flush
        // against the footer's solid band. 20px matches the gap above it — the
        // padding-bottom on .title-map-list-toggle — and the 20px the list view
        // already leaves before the footer, so the two views agree.
        margin-bottom: 20px;

        .download-button {
            position: absolute;
            top: 16px;
            right: 16px;
            z-index: 1;
            border-radius: 3.333em;
            border: 2px solid white;
            background-color: white;
            transition: all 0.25s ease;

            &:hover {
                color: white;
                background-color: $turquoise;
                box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
            }
        }
    }
</style>
